import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { ComposableMap, Geographies, Geography, Sphere } from "react-simple-maps";
import countriesIso from "i18n-iso-countries";

export default function VisitedCountriesMap({ countries = [], onCountryClick }) {
  const visitedSet = useMemo(
    () => new Set(countries.map((c) => (c || "").toUpperCase())),
    [countries]
  );

  const [rotation, setRotation] = useState([0, 0, 0]);
  const [scale, setScale] = useState(350);
  const [isDragging, setIsDragging] = useState(false);

  const rotationRef = useRef([0, 0, 0]);
  const targetRotationRef = useRef([0, 0, 0]);
  const isDraggingRef = useRef(false);
  const lastPointerPosRef = useRef(null);
  const rafRef = useRef(null);
  const scaleRef = useRef(350);
  const pointersRef = useRef(new Map());
  const isPinchingRef = useRef(false);
  const pinchStartDistanceRef = useRef(0);
  const pinchStartScaleRef = useRef(350);
  const containerRef = useRef(null);

  useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);

  useEffect(() => () => rafRef.current && cancelAnimationFrame(rafRef.current), []);

  const BASE_SPEED = 0.3;
  const MIN_SCALE = 200;
  const MAX_SCALE = 5000;
  const BASE_SCALE = 350;
  const MIN_SPEED = 0.05;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const getDynamicSpeed = (currentScale) => {
    const s = clamp(currentScale ?? BASE_SCALE, MIN_SCALE, MAX_SCALE);
    const factor = BASE_SCALE / s;
    const spd = BASE_SPEED * factor;
    return clamp(spd, MIN_SPEED, BASE_SPEED);
  };

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  const loop = useCallback(() => {
    const [tx, ty] = targetRotationRef.current;
    const next = [tx, ty, 0];
    rotationRef.current = next;
    setRotation(next);

    if (isDraggingRef.current) {
      rafRef.current = requestAnimationFrame(loop);
    }
    else {
      rafRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback((e) => {
    setIsDragging(true);
    isDraggingRef.current = true;
    lastPointerPosRef.current = { x: e.clientX, y: e.clientY };
    targetRotationRef.current = rotationRef.current.slice();
    try {
      e.target.setPointerCapture?.(e.pointerId);
    } catch {}
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      pinchStartDistanceRef.current = Math.hypot(dx, dy) || 1;
      pinchStartScaleRef.current = scaleRef.current;
      isPinchingRef.current = true;
      isDraggingRef.current = false;
    }
    if (!rafRef.current) rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  const onPointerMove = useCallback((e) => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }

    if (isPinchingRef.current && pointersRef.current.size >= 2) {
      const pts = Array.from(pointersRef.current.values());
      const dx = pts[0].x - pts[1].x;
      const dy = pts[0].y - pts[1].y;
      const dist = Math.hypot(dx, dy) || 1;
      const factor = dist / pinchStartDistanceRef.current;
      const next = clamp(pinchStartScaleRef.current * factor, MIN_SCALE, MAX_SCALE);
      if (next !== scaleRef.current) setScale(next);
      return;
    }

    if (!isDraggingRef.current || !lastPointerPosRef.current) return;
    const { x, y } = lastPointerPosRef.current;
    const dx = e.clientX - x;
    const dy = e.clientY - y;
    lastPointerPosRef.current = { x: e.clientX, y: e.clientY };

    const [tx, ty] = targetRotationRef.current;
    const speed = getDynamicSpeed(scaleRef.current);
    targetRotationRef.current = [tx + dx * speed, clamp(ty - dy * speed, -89, 89), 0];
  }, []);

  const endDrag = useCallback((e) => {
    setIsDragging(false);
    isDraggingRef.current = false;
    lastPointerPosRef.current = null;
    try {
      e.target.releasePointerCapture?.(e.pointerId);
    } catch {}
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) {
      isPinchingRef.current = false;
    }
  }, []);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const factor = Math.exp(-e.deltaY * 0.002);
    const next = clamp(scaleRef.current * factor, MIN_SCALE, MAX_SCALE);
    if (next !== scaleRef.current) setScale(next);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const wheelHandler = (e) => onWheel(e);
    const touchMoveHandler = (e) => {
      if (isPinchingRef.current || (e.touches && e.touches.length > 1)) {
        e.preventDefault();
      }
    };
    const gestureHandler = (e) => {
      e.preventDefault();
    };

    el.addEventListener("wheel", wheelHandler, { passive: false });
    el.addEventListener("touchmove", touchMoveHandler, { passive: false });
    el.addEventListener("gesturestart", gestureHandler, { passive: false });
    el.addEventListener("gesturechange", gestureHandler, { passive: false });
    el.addEventListener("gestureend", gestureHandler, { passive: false });

    return () => {
      el.removeEventListener("wheel", wheelHandler, { passive: false });
      el.removeEventListener("touchmove", touchMoveHandler, { passive: false });
      el.removeEventListener("gesturestart", gestureHandler, { passive: false });
      el.removeEventListener("gesturechange", gestureHandler, { passive: false });
      el.removeEventListener("gestureend", gestureHandler, { passive: false });
    };
  }, [onWheel]);

  return (
    <div
      ref={containerRef}
      className="w-full mx-auto max-w-3xl h-full bg-white dark:bg-darkBackground flex items-center justify-center overflow-hidden"
      style={{ touchAction: "none", overscrollBehavior: "none", cursor: isDragging ? "grabbing" : "grab" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={endDrag}
    >
      <ComposableMap
        projection="geoOrthographic"
        projectionConfig={{ scale, rotate: rotation }}
        shapeRendering="optimizeSpeed"
        className="w-full h-full"
      >
        <Sphere fill="#0d3b66" stroke="#0d3b66" />
          <Geographies geography="/assets/map/countries.json">
            {({ geographies }) =>
              geographies.map((geo) => {
                const iso2Raw =
                  geo.properties?.["ISO3166-1-Alpha-2"] ||
                  countriesIso.numericToAlpha2(String(geo.id)) ||
                  geo.properties?.iso_a2;
                const iso2 = (iso2Raw || "").toUpperCase();

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={visitedSet.has(iso2) ? "#48c9b0" : "#006a53"}
                    stroke="#0d3b66"
                    strokeWidth={2.5}
                    onClick={() => {
                      if (visitedSet.has(iso2) && typeof onCountryClick === "function") {
                        onCountryClick(iso2);
                      }
                    }}
                    style={{
                      default: { outline: "none", cursor: visitedSet.has(iso2) ? "pointer" : "default" },
                      hover: { outline: "none", cursor: visitedSet.has(iso2) ? "pointer" : "default" },
                      pressed: { outline: "none", cursor: visitedSet.has(iso2) ? "pointer" : "default" },
                    }}
                  />
                );
              })
            }
          </Geographies>
      </ComposableMap>
    </div>
  );
}
